import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { sortBy } from 'lodash';
import moment from 'moment';

import { InfoButtonWithOnlineHelp, LoadingStatusWrapper, ScrollableList } from 'sharedComponents';
import { IconCondition, IconTrendPoorIndicator, IconTrendTolerableIndicator, IconConditionUnknown, IconGoodCondition, IconAnomalyDetection } from 'svgIcons/MotionPortalIcons';
import { SmartConditionSummary } from './SmartConditionSummary';
import AnomalyDetection from './AnomalyDetection';
import { setActiveTab as setConditionInsightsActiveTab, setDriveAnomalyActiveTab, setClickedConditionIndex } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/actions';
import { ENERGY_ANOMALY_DETECTION_KEY, driveAnomalyTabs } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/constants';
import './style.scss';
import { assetCondition, conditionIndices, maxAnomalyLevel } from './constants';
import { setActiveTabV2 } from '../../PowertrainAssetDetails/DetailedInformation/actions';
import { tabIDs } from '../../PowertrainAssetDetails/DetailedInformation/constants';
import { ASSET_TYPES, newConditionIndices } from 'helpers/constants';
import { priorityCondition } from './helpers';
import DriveEventsSummaryTable from './DriveEventsSummaryTable';
import { getAssetTypeId } from 'helpers/assetAndPowertrainFilterHelper';
import useUserCapability from 'sharedHooks/useUserCapability';
import { useNewCapability } from 'sharedHooks';
import { assetStatus } from '../../../../ClientApp/components/PowertrainFleets/FleetsDashboard/constants';
import IconRightArrow from 'svgIcons/MotionPortalIcons/IconRightArrow';
import { setClickedLimitFromSummary } from '../../PowertrainAssetDetails/DetailedInformation/OperationalParametersV2/SingleChartView/Trend/actions';
import { EventTypeIDs } from '../../PowertrainAssetDetails/DetailedInformation/EventLog/constants';
import { setFilters } from '../../PowertrainAssetDetails/DetailedInformation/EventLog/EventLogFilter/actions';
import { conditionInsightsTabs } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/helpers';

const AssetSummaryComponent = ({ asset, isUnmonitored }) => {
    const { t: translate } = useTranslation();
    const userCapabilities = useUserCapability();
    const newCapabilities = useNewCapability();
    const hasConditionsummaryCapability = newCapabilities.hasMotorCapraRecommendationsCapability();
    const dispatch = useDispatch();
    const assetDetailsLabelsLoadingStatus = useSelector(({ assetDetailsInfoHeader }) => assetDetailsInfoHeader?.labels?.loadingStatus);
    const subscriptionLoadingStatus = useSelector(({ powertrainTools }) => powertrainTools?.subscriptionStatus?.subscriptionArr?.loadingStatus);
    const driveEventsSummary = useSelector(({ assetOverviewV2 }) => assetOverviewV2?.driveEventsSummary);
    const smartConditionSummaryData = useSelector(({ assetOverviewV2 }) => assetOverviewV2?.smartConditionSummary?.smartConditionSummary);
    if (!asset) {
        return (
            <LoadingStatusWrapper loadingStatus={[subscriptionLoadingStatus, assetDetailsLabelsLoadingStatus]} data-testid='loading-wrapper' />
        );
    }
    const { condition, limits, threshold, trend } = asset;
    const anomalyDetectionKPIs = trend?.KPIs?.filter(kpi => kpi?.isAnomaly) ?? [];
    const getAnomalyDetectionKPI = trend?.KPIs?.find(kpi => kpi?.isAnomaly);

    const handleNavigationToConditionInsightsAnomaly = () => {
        dispatch(setActiveTabV2(tabIDs.conditionInsights));
        // Set the Condition Insights toggle to Anomaly Detection (sub tab)
        dispatch(setConditionInsightsActiveTab(conditionInsightsTabs.ANOMALY_DETECTION));
    };

    const handleAnomalyClick = (kpi) => {
        handleNavigationToConditionInsightsAnomaly();
        // Navigate to the specific drive anomaly sub-tab matching the clicked KPI
        if (kpi?.key === ENERGY_ANOMALY_DETECTION_KEY) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.ENERGY));
        } else {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.COOLING));
        }
    };

    const renderNavigationIcon = () => {
        return (
            <div className='asset-overview-params__navigation'>
                <IconRightArrow />
            </div>
        );
    };

    const renderHeader = (isLimits) => {
        return <div className='asset-overview-params-header-v2'>
            <div className='header-item'>
                {isLimits ? translate('ABB.Powertrain.Frontend.assetOVerviewConditionLimitLabel') : translate('ABB.Powertrain.Frontend.conditionIndicesTab')}
            </div>
            <div className='header-item'>
                {translate('ABB.Powertrain.Frontend.assetOverviewDescriptionLabel')}
            </div>
        </div>;
    };

    const getConditionDescription = (condition, isLimits, source) => {
        // If it's limits, use the standard limit crossing labels
        if (isLimits) {
            switch (condition) {
                case assetCondition.CONDITION_ALARM:
                    return `${translate('ABB.Powertrain.Frontend.assetOverviewAlarmLabel')}: ${translate('ABB.Powertrain.Frontend.assetOverviewLimitCrossingLabel')}`;
                case assetCondition.CONDITION_ALERT:
                    return `${translate('ABB.Powertrain.Frontend.assetOverviewAlertLabel')}: ${translate('ABB.Powertrain.Frontend.assetOverviewLimitCrossingLabel')}`;
                default:
                    return '';
            }
        }

        // For condition indices, differentiate between threshold and trend
        const crossingLabel = source === conditionIndices.TREND
            ? translate('ABB.Powertrain.Frontend.assetOverviewTrendCrossingLabel')
            : translate('ABB.Powertrain.Frontend.assetOverviewThresholdCrossingLabel');
        switch (condition) {
            case assetCondition.CONDITION_POOR:
                return `${translate('ABB.Powertrain.Frontend.assetOverviewAlarmLabel')}: ${crossingLabel}`;
            case assetCondition.CONDITION_TOLERABLE:
                return `${translate('ABB.Powertrain.Frontend.assetOverviewAlertLabel')}: ${crossingLabel}`;
            default:
                return '';
        }
    };

    const renderGoodCondition = () => {
        return (
            <div className='render-good__condition'>
                <IconGoodCondition />
                <p>{translate('ABB.Powertrain.Frontend.assetOverviewAssetAreGoodCondition')}</p>
            </div>
        );
    };

    const renderNoConditionIndices = () => {
        return (
            <div className='render-no__condition-indices'>
                <IconGoodCondition />
                <p>{translate('ABB.Powertrain.Frontend.assetOverviewNoAlarmAlertConditionIndices')}</p>
            </div>
        );
    };

    const renderNoAssetLimits = () => {
        return (
            <div className='render-no__asset-limits'>
                <IconGoodCondition />
                <p>{translate('ABB.Powertrain.Frontend.assetOverviewNoAlarmAlertLimitCrossing')}</p>
            </div>
        );
    };

    const renderNoAnomalyDetected = () => {
        return (
            <div className='render-no__anomaly-detected'>
                <IconGoodCondition />
                <p>{translate('ABB.Powertrain.Frontend.anomalyDetectEmptyLabel')}</p>
            </div>
        );
    };

    const renderUnknownAnomalyDetected = () => {
        return (
            <div className='render-no__anomaly-detected'>
                <IconConditionUnknown />
                <p>{translate('ABB.Powertrain.Frontend.anomalyUnknownLabel')}</p>
            </div>
        );
    };

    const handleConditionIndicesNavigation = (kpi, isLimits, isAnomaly) => {
        if (kpi) {
            if (isLimits) {
                dispatch(setClickedLimitFromSummary(kpi));
                dispatch(setActiveTabV2(tabIDs.operationalParameters));
            } else if (isAnomaly) {
                handleNavigationToConditionInsightsAnomaly();
            } else {
                dispatch(setClickedConditionIndex(kpi));
                dispatch(setActiveTabV2(tabIDs.conditionInsights));
            }
        }
    };

    const renderConditionItem = (condition, isLimits, isAnomaly) => {
        return (
            <button
                className='asset-overview-params__list list-condition-item'
                key={condition.id}
                onClick={() => handleConditionIndicesNavigation(condition.id, isLimits, isAnomaly)}
            >
                <div className='asset-overview-params__kpi'>
                    <span className='asset-overview-params__kpi-icon'>{condition.kpi.icon}</span>
                    {condition.kpi.name}
                </div>
                <div className='asset-overview-params__description'>
                    {condition.description}
                    {renderNavigationIcon()}
                </div>
            </button>
        );
    };

    const renderConditionIndicesIcon = (condition, source, iconCondition) => {
        if (condition?.source === conditionIndices.TREND || source === conditionIndices.TREND) {
            if (iconCondition === assetCondition.CONDITION_POOR) {
                return <IconTrendPoorIndicator />;
            } else if (iconCondition === assetCondition.CONDITION_TOLERABLE) {
                return <IconTrendTolerableIndicator />;
            } else {
                return null;
            }
        } else {
            return <IconCondition condition={newConditionIndices[iconCondition]?.iconID} />;
        }
    };

    const renderConditionList = (data, isLimits, source) => {
        if (!data || !Array.isArray(data) || !data.length) {
            return isLimits ? renderNoAssetLimits() : renderNoConditionIndices();
        }

        const filteredConditions = data.filter(item =>
            !item.isAnomaly &&
            (isLimits
                ? item.condition === assetCondition.CONDITION_ALARM || item.condition === assetCondition.CONDITION_ALERT
                : item.condition === assetCondition.CONDITION_POOR || item.condition === assetCondition.CONDITION_TOLERABLE
            ));


        if (!filteredConditions.length) {
            return isLimits ? renderNoAssetLimits() : renderNoConditionIndices();
        }

        const conditionsWithPriority = filteredConditions.map(condition => {
            const priority = priorityCondition[condition?.condition];

            return {
                ...condition,
                priority,
                sortName: condition.name || condition.timeseriesName
            };
        });

        const sortedConditions = sortBy(conditionsWithPriority, ['priority', 'sortName']);
        const conditionData = sortedConditions.map(condition => {
            let iconCondition;
            if (isLimits) {
                iconCondition = condition.condition === assetCondition.CONDITION_ALARM ? assetCondition.CONDITION_POOR : assetCondition.CONDITION_TOLERABLE;
            } else {
                iconCondition = condition.condition;
            }

            const conditionLimitsName = condition?.timeseriesName && !isNaN(condition?.timeseriesKey) ? `${condition?.timeseriesKey} ${translate(condition?.timeseriesNameTextKey)}` : translate(condition?.timeseriesNameTextKey);
            const itemSource = condition.source || source;

            return {
                id: condition?.timeseriesKey || condition?.key,
                kpi: {
                    icon: renderConditionIndicesIcon(condition, source, iconCondition),
                    name: conditionLimitsName || condition?.name
                },
                description: getConditionDescription(condition?.condition, isLimits, itemSource)
            };
        });

        return (
            <>
                {renderHeader(isLimits)}
                <ScrollableList>
                    {conditionData.map((condition) => renderConditionItem(condition, isLimits, false))}
                </ScrollableList>
            </>
        );
    };

    const renderLimits = () => renderConditionList(limits?.Limits, true);
    const renderConditionIndices = () => {
        const isUnsupported = (!threshold?.condition || threshold?.condition === assetCondition.CONDITION_UNSUPPORTED) &&
            (!trend?.condition || trend?.condition === assetCondition.CONDITION_UNSUPPORTED);
        const isUnknownCondition = condition === assetCondition.CONDITION_UNKNOWN;
        if (isUnsupported) {
            // Do not render the "Condition Insights" column if unsupported
            return null;
        }
        if (isUnknownCondition) {
            return (
                <div className='render-no__condition-indices'>
                    <IconConditionUnknown />
                    <p>{translate('ABB.Powertrain.Frontend.assetOverviewConditionIndicesStatusUnknown')}</p>
                </div>
            );
        }
        const thresholdKPIs = threshold?.KPIs || [];
        const trendKPIs = trend?.KPIs || [];
        if (thresholdKPIs.length && trendKPIs.length) {
            const thresholdWithSource = thresholdKPIs.map(item => ({ ...item, source: conditionIndices.THRESHOLD }));
            const trendWithSource = trendKPIs.map(item => ({ ...item, source: conditionIndices.TREND }));
            return renderConditionList([...thresholdWithSource, ...trendWithSource], false);
        } else if (thresholdKPIs.length) {
            return renderConditionList(thresholdKPIs, false, conditionIndices.THRESHOLD);
        } else if (trendKPIs.length) {
            return renderConditionList(trendKPIs, false, conditionIndices.TREND);
        } else {
            return renderConditionList([], false);
        }
    };
    // Render Anomaly Detection section for Motor assets
    const renderAnomalyDetected = () => {
        if (getAnomalyDetectionKPI?.condition === assetCondition.CONDITION_UNKNOWN) {
            return renderUnknownAnomalyDetected();
        }

        if (!getAnomalyDetectionKPI?.anomalyLevel) {
            return renderNoAnomalyDetected();
        }

        const conditionData = [{
            id: getAnomalyDetectionKPI?.key,
            kpi: {
                icon: <IconAnomalyDetection height={18} width={18} viewBox={'0 0 18 18'} />,
                name: translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAnomalyColumnTitle')
            },
            description: `${translate('ABB.Powertrain.Frontend.levelLabel')}: ${getAnomalyDetectionKPI?.anomalyLevel ?? '-'}/${maxAnomalyLevel}`
        }];

        return (
            <ScrollableList>
                {conditionData.map((condition) => renderConditionItem(condition, false, true))}
            </ScrollableList>
        );
    };

    const handleEventNavigation = () => {
        const to = moment().endOf('day').toISOString();
        const from = moment().subtract(6, 'days').startOf('day').toISOString();
        const filters = {
            eventTypes: [EventTypeIDs.FAULT, EventTypeIDs.WARNING],
            dateSelection: {
                from,
                to
            },
        };
        // Navigate to details page and set the active tab to drive events
        dispatch(setActiveTabV2(tabIDs.eventLog));
        dispatch(setFilters(filters));
    };

    if (isUnmonitored) {
        return (
            <LoadingStatusWrapper loadingStatus={[subscriptionLoadingStatus, assetDetailsLabelsLoadingStatus]}>
                <div className='asset-overview-params-v2__container'>
                    <div className='asset-overview-params'>
                        <div className='asset-overview-params__header'>
                            <h2 className='asset-overview-params__heading'>{translate('ABB.Powertrain.Frontend.assetOverviewLimitsText')}</h2>
                        </div>
                        <div className='asset-overview-params__table'>
                            {renderLimits()}
                        </div>
                    </div>
                </div>
            </LoadingStatusWrapper>
        );
    }

    return (
        <LoadingStatusWrapper loadingStatus={[subscriptionLoadingStatus, assetDetailsLabelsLoadingStatus]}>
            <div className='asset-overview-params-v2__container'>
                {condition === assetCondition.CONDITION_GOOD ? renderGoodCondition()
                    : <>
                        {renderConditionIndices() &&
                            <div className='asset-overview-params'>
                                <div className='asset-overview-params__header'>
                                    <h2 className='asset-overview-params__heading'>{translate('ABB.Powertrain.Frontend.conditionIndicesTab')}</h2>
                                </div>
                                <div className='asset-overview-params__table'>
                                    {renderConditionIndices()}
                                </div>
                            </div>
                        }

                        {/* Smart Condition Summary Component */}
                        {hasConditionsummaryCapability && smartConditionSummaryData &&
                            <SmartConditionSummary
                                smartConditionSummaryData={smartConditionSummaryData}
                            />
                        }

                        {getAssetTypeId(asset?.assetType) === ASSET_TYPES.MOTOR &&
                            userCapabilities.hasMotorAnomalyDetection() &&
                            getAnomalyDetectionKPI &&
                            <div className='asset-overview-params'>
                                <div className='asset-overview-params__header'>
                                    <h2 className='asset-overview-params__heading'>{translate('ABB.Powertrain.Frontend.anomalyDetectionTab')}</h2>
                                </div>
                                <div className='asset-overview-params__table anomaly-detection-table'>
                                    {renderAnomalyDetected()}
                                </div>
                            </div>
                        }
                        {getAssetTypeId(asset?.assetType) === ASSET_TYPES.DRIVE &&
                            <>
                                {userCapabilities.hasDriveEventsSummary() && driveEventsSummary &&
                                    <div className='asset-overview-params'>
                                        <button className='asset-overview-params__header with-navigation' onClick={() => handleEventNavigation()} style={{ cursor: 'pointer' }}>
                                            <h2 className='asset-overview-params__heading'>
                                                {translate('ABB.Powertrain.Frontend.driveEventsTitle')} ({translate('ABB.Powertrain.Frontend.assetSummaryEventCurrentDay')})
                                                <InfoButtonWithOnlineHelp
                                                    infoText={translate('ABB.Powertrain.Frontend.assetSummaryEventCurrentDayInfo')}
                                                    placement='top'
                                                    withoutLink
                                                />
                                            </h2>
                                            <div className='asset-overview-params__navigation'>
                                                <IconRightArrow />
                                            </div>
                                        </button>
                                        <div className='asset-overview-params__table drive-events-table'>
                                            <DriveEventsSummaryTable />
                                        </div>
                                    </div>
                                }
                                {/* Anomaly Detection UI for Drive assets */}
                                {(userCapabilities.hasDriveAnomalyDetection() || userCapabilities.hasDriveEnergyOutlierDetection() || userCapabilities.hasDriveACS1000AnomalyDetection()) &&
                                    anomalyDetectionKPIs.length > 0 &&
                                    trend?.condition !== assetStatus.UNSUPPORTED && <div className='asset-overview-params'>
                                        <div className='asset-overview-params__header'>
                                            <h2 className='asset-overview-params__heading'>
                                                {translate('ABB.Powertrain.Frontend.anomalyDetectionTab')}
                                                <InfoButtonWithOnlineHelp
                                                    infoText={
                                                        <div className='asset-overview-params-tooltip-container'>
                                                            <h6 className='asset-overview-params-tooltip-heading'>
                                                                {translate('ABB.Powertrain.Frontend.driveAnomalyTooltipTitle')}
                                                            </h6>
                                                            <p className='asset-overview-params-tooltip-text'>
                                                                {translate('ABB.Powertrain.Frontend.driveAnomalyTooltipIntro')}
                                                            </p>
                                                            <ul className='asset-overview-params-tooltip-list'>
                                                                <li><strong>{translate('ABB.Powertrain.Frontend.coolingAnomalyDetection')}:</strong> {translate('ABB.Powertrain.Frontend.driveAnomalyTooltipCooling')}</li>
                                                                <li><strong>{translate('ABB.Powertrain.Frontend.energyAnomalyDetection')}:</strong> {translate('ABB.Powertrain.Frontend.driveAnomalyTooltipEnergy')}</li>
                                                            </ul>
                                                        </div>
                                                    }
                                                    placement='top'
                                                    withoutLink
                                                />
                                            </h2>
                                        </div>
                                        <div className='asset-overview-params__table anomaly-detection-table'>
                                            <AnomalyDetection
                                                anomalyKPIs={anomalyDetectionKPIs}
                                                maxLevel={5}
                                                onClick={handleAnomalyClick} />
                                        </div>
                                    </div>}
                            </>
                        }
                        <div className='asset-overview-params'>
                            <div className='asset-overview-params__header'>
                                <h2 className='asset-overview-params__heading'>{translate('ABB.Powertrain.Frontend.assetOverviewLimitsText')}</h2>
                            </div>
                            <div className='asset-overview-params__table'>
                                {renderLimits()}
                            </div>
                        </div>
                    </>}
            </div >
        </LoadingStatusWrapper >
    );
};


AssetSummaryComponent.propTypes = {
    actions: PropTypes.shape({
        getSubscriptionInfo: PropTypes.func,
        getApiAssetCapability: PropTypes.func,
        getAssetOverview: PropTypes.func,
        getAssetLabels: PropTypes.func
    }),
    asset: PropTypes.shape({
        assetId: PropTypes.number,
        assetSerialNumber: PropTypes.string,
        assetName: PropTypes.string,
        assetType: PropTypes.string,
        conditionIndex: PropTypes.object,
        condition: PropTypes.string,
        limits: PropTypes.object,
        threshold: PropTypes.object,
        trend: PropTypes.object,
    }),
    model: PropTypes.shape({
        allAssets: PropTypes.array,
        newassetCapability: PropTypes.object,
        productTypeCode: PropTypes.object,
        assetLabels: PropTypes.array,
        assetOnline: PropTypes.string,
        connectivityType: PropTypes.string,
        subscriptionLoadingStatus: PropTypes.string,
        lastStatusChangeRecievedTimestamp: PropTypes.string,
        assetDetailsLabelsLoadingStatus: PropTypes.string,
        subscriptionStatus: PropTypes.array
    }),
    data: PropTypes.array,
    selectedPowertrain: PropTypes.string,
    assetGroup: PropTypes.number,
    isUnmonitored: PropTypes.bool,
};

export default AssetSummaryComponent;
